import { useMemo, useState } from 'react'
import { useNotification } from 'hooks'
import { Table } from 'components/core-ui/Table'
import { columns, getFilters, toolbarActions } from './helpers'
import { makeCoreGetFileRequest, makeCoreGetRequest } from "utils/api"
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { formatAndDownloadResponse } from 'utils/helpers'
import s from '../../../commonStyles.modules.scss'
import {
  getDsSupportTypeFilterOptions,
  getDsSupportTypeOptions,
  getParentSocietyOptions,
  getScreenPermissions,
  getShopOptions
} from "../../api"
import { SCREENS, URLS } from "../../constants"
import { EditActionIcon } from "../../../../../icons"
import { History } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { EditBalancePackaging } from "./EditBalancePackaging"
import { extractShopCode } from "./EditBalancePackaging/helpers"

const BalancePackaging = () => {
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<any>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isCSVExportLoading, setIsCSVExportLoading] = useState<boolean>(false)
  const [modalMode, setModalMode] = useState<'edit' | 'add'>('edit')

  const { showNotification } = useNotification()
  const navigate = useNavigate()

  const { data: shopOptions } = getShopOptions(showNotification)
  const { data: parentSocietyOptions } = getParentSocietyOptions(showNotification)
  const { data: supportTypeOptions } = getDsSupportTypeOptions(showNotification)
  const { data: supportTypeFilterOptions } = getDsSupportTypeFilterOptions(showNotification)

  const { data: permissions } = getScreenPermissions(
    showNotification,
    SCREENS.PACKAGING_SUPPORT_STOCK
  )

  const filters = useMemo(
    () => getFilters({ shopOptions, supportTypeFilterOptions, parentSocietyOptions }),
    [shopOptions, supportTypeFilterOptions, parentSocietyOptions]
  )

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/shop-reception/ds-support-stock',
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor din tabel. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const exportFileCSV = async (tableState) => {
    setIsCSVExportLoading(true)
    const { filters, order, orderBy } = tableState
    const url = toTableGETUrl({
      url: '/shop-reception/ds-support-stock/export',
      tableFilters: filters,
      order,
      orderBy
    })

    try {
      const response = await makeCoreGetFileRequest(url, {
        responseType: 'blob'
      })
      formatAndDownloadResponse(response)
      setIsCSVExportLoading(false)
    } catch (error) {
      setIsCSVExportLoading(false)
      showNotification({
        message: `A aparut o eroare la descarcarea fisierului. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const openModal = () => setIsEditModalOpen(true)
  const closeModal = () => {
    setIsEditModalOpen(false)
    setSelectedRow(null)
  }

  const selectRow = (rowId: number) => {
    const row = tableItems.find(item => item.id === rowId)
    setSelectedRow(row)
    setModalMode('edit')
    openModal()
  }

  const openAdd = () => {
    setSelectedRow(null)
    setModalMode('add')
    openModal()
  }

  const toolbar = useMemo(() => toolbarActions({
      canDownload: permissions?.canDownload,
      canAdd: permissions?.canAdd,
      openAdd,
      totalCount,
      exportFileCSV: exportFileCSV,
      isCSVExportLoading }),
    [totalCount, isCSVExportLoading, permissions])

  const displayHistory = (rowId: number) => {
    const row = tableItems.find(item => item.id === rowId)
    if (!row) return

    const numericShopCode = Number(extractShopCode(row.shopCode, { digitsOnly: true }))

    navigate(
      URLS.AUTH_SUPPLIERS_BALANCE_PACKAGING_HISTORY.replace(':id', `${rowId}`),
      {
        state: {
          shopCode: numericShopCode,
          supplierCode: row.supplierCode,
          dsSupportTypeId: row.supportId,
        }
      }
    )
  }

  return (
    <div className={s.mainContainer}>
      <EditBalancePackaging
        isModalOpen={isEditModalOpen}
        closeModal={closeModal}
        editItem={selectedRow}
        supportTypeOptions={supportTypeOptions}
        shopOptions={shopOptions}
        mode={modalMode}
        setShouldRefreshData={setShouldRefreshData}
      />
      <Table
        hideCheckbox
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        orderBy="lastUpdate"
        order="desc"
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        rowActions={
          permissions?.canEdit
            ? [
              {
                label: 'Editeaza',
                labelColor: 'white',
                typographyVariant: 'medium',
                backgroundColor: '#16a085',
                desktopIcon: <EditActionIcon />,
                onClick: (_e, rowId: number) => selectRow(rowId)
              },
              {
                label: 'Date istorice',
                labelColor: 'white',
                typographyVariant: 'medium',
                backgroundColor: '#d9534f',
                desktopIcon: <History htmlColor="rgb(9, 112, 230)" />,
                onClick: (_e, rowId: number) => displayHistory(rowId)
              }
            ]
            : []
        }
        toolbarActions={toolbar}
        filters={filters}
        filtersButtonLabel="Filtre"
        filtersLabel="Filtre"
        appliedFiltersLabel="Filtre aplicate"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
        rowsPerPage={10}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </div>
  )
}

export default BalancePackaging
